import React from 'react';
import { Typography, Tag, Space, Avatar, Empty, Progress } from 'antd';
import { TrophyOutlined, UserOutlined } from '@ant-design/icons';
import { formatDisplayName } from '../utils/moduleUtils';
import { LeaderboardEntry } from '../types/training';

const { Title, Text } = Typography;

const THEME = {
    trainerLed: {
        gradient: 'linear-gradient(135deg, #f0f9ff 0%, #e6f4ff 100%)',
        border: '#91caff',
        rowBorder: '#bae0ff',
        title: '#0958d9',
        icon: '#1677ff',
        tagColor: 'blue',
        topAvatar: '#1677ff',
        avatar: '#8c8c8c',
    },
    aiLed: {
        gradient: 'linear-gradient(135deg, #fff7e6 0%, #fff0d9 100%)',
        border: '#ffd591',
        rowBorder: '#ffe7ba',
        title: '#d46b08',
        icon: '#fa8c16',
        tagColor: 'orange',
        topAvatar: '#fa541c',
        avatar: '#1890ff',
    },
} as const;

interface LeaderboardProps {
    entries: LeaderboardEntry[];
    variant: 'trainerLed' | 'aiLed';
    emptyDescription: string;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ entries, variant, emptyDescription }) => {
    const theme = THEME[variant];
    const isTrainerLed = variant === 'trainerLed';

    if (entries.length === 0) {
        return <Empty description={emptyDescription} style={{ padding: '60px 0' }} />;
    }

    return (
        <div style={{
            background: theme.gradient,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: '20px 24px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
                <TrophyOutlined style={{ fontSize: 20, color: theme.icon }} />
                <Title level={4} style={{ margin: 0, color: theme.title }}>Program Leaderboard</Title>
                <Tag color={theme.tagColor} style={{ marginLeft: 4 }}>{entries.length} participants</Tag>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#8c8c8c', width: 54 }}>RANK</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#8c8c8c' }}>PARTICIPANT</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#8c8c8c', width: 110 }}>
                                {isTrainerLed ? 'GRADED' : 'MODULES'}
                            </th>
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: '#8c8c8c', width: 130 }}>
                                {isTrainerLed ? 'CREDITS' : 'AVG SCORE'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, idx) => {
                            const rank = idx + 1;
                            const isTop3 = rank <= 3;
                            const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
                            const pct = isTrainerLed && entry.maxscore ? Math.round((entry.totalscore / entry.maxscore) * 100) : null;
                            return (
                                <tr key={entry.email} style={{ borderBottom: `1px solid ${theme.rowBorder}`, background: isTop3 ? 'rgba(255,255,255,0.6)' : 'transparent' }}>
                                    <td style={{ padding: '10px 12px', fontSize: 18, textAlign: 'center' }}>
                                        {medal ?? <Text type="secondary" style={{ fontSize: 13 }}>#{rank}</Text>}
                                    </td>
                                    <td style={{ padding: '10px 12px' }}>
                                        <Space>
                                            <Avatar icon={<UserOutlined />} style={{ backgroundColor: isTop3 ? theme.topAvatar : theme.avatar, flexShrink: 0 }} />
                                            <div>
                                                <div style={{ fontWeight: isTop3 ? 700 : 500, fontSize: 14 }}>{formatDisplayName(entry.fullname, entry.email)}</div>
                                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{entry.email}</div>
                                            </div>
                                        </Space>
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                        {isTrainerLed ? (
                                            <Tag color="blue">{entry.attempts ?? 0}</Tag>
                                        ) : (
                                            <Tag color="green">{entry.modulescompleted ?? 0}/{entry.totalmodules ?? '?'}</Tag>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                        {isTrainerLed ? (
                                            <div>
                                                <Text strong style={{ fontSize: 15, color: isTop3 ? theme.title : '#595959' }}>
                                                    {entry.totalscore.toFixed(0)}
                                                    {entry.maxscore ? <Text type="secondary" style={{ fontSize: 12 }}> / {entry.maxscore.toFixed(0)} pts</Text> : ' pts'}
                                                </Text>
                                                {pct !== null && (
                                                    <Progress percent={pct} size="small" showInfo={false} strokeColor={pct >= 70 ? '#52c41a' : pct >= 40 ? '#fa8c16' : '#ff4d4f'} style={{ marginBottom: 0, marginTop: 2 }} />
                                                )}
                                            </div>
                                        ) : (
                                            <Text strong style={{ fontSize: 16, color: isTop3 ? theme.title : '#595959' }}>
                                                {entry.totalscore.toFixed(1)}%
                                            </Text>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Leaderboard;
